import {
  PostCompany,
  GetCompanies,
  GetCompanyFundedSubscriptions,
  PutCompany,
  GetCheckIfCompanyExists,
} from "@/server-types";
import { post, get, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";
import { useInfiniteQuery } from "@tanstack/react-query";

export const usePostCompany = () => {
  return useCustomMutation({
    mutationFn: (body: PostCompany["body"]) =>
      post<PostCompany>("/company", body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

export const useGetCompanies = (
  query: GetCompanies["query"] & {
    enabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["companies", query],
    queryFn: () => get<GetCompanies>("/company", query),
    enabled: query.enabled ?? true,
  });
};

export const useInfiniteGetCompanies = (query: GetCompanies["query"]) => {
  return useInfiniteQuery({
    queryKey: ["getCompaniesInfinite", query],
    queryFn: async ({ pageParam = 0 }) => {
      return get<GetCompanies>("/company", {
        ...query,
        pagination: {
          ...query.pagination,
          offset: pageParam as number,
        },
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
  });
};

export const usePutCompany = () => {
  return useCustomMutation({
    mutationFn: ({
      companyId,
      body,
    }: {
      companyId: number;
      body: PutCompany["body"];
    }) => put<PutCompany>(`/company/${companyId}`, body),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId] });
    },
  });
};

export const useGetCheckIfCompanyExists = (
  query: GetCheckIfCompanyExists["query"],
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["useGetCheckIfCompanyExists", query],
    queryFn: () =>
      get<GetCheckIfCompanyExists>("/company/check-if-exists", query),
    enabled: enabled ?? true,
  });
};

export const useGetCompanyFundedSubscriptions = (
  companyId: number | null,
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["company-funded-subscriptions", companyId],
    queryFn: () =>
      get<GetCompanyFundedSubscriptions>(
        `/company/${companyId ?? 0}/funded-subscriptions`,
      ),
    enabled: (enabled ?? true) && companyId != null,
  });
};
